/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import EditIcon from "@mui/icons-material/Edit";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import NavLinks from "../NavLinks";
import useIsMobileViewport from "../../../../hooks/useIsMobileViewport";
import VersionsDialog, { IVersionsObject } from "./VersionsDialog";
import { useAppContext } from "../../../../contexts/useStorageContext";
import { UserRoles } from "../../../../types/auth";
import IconButton from "@mui/material/IconButton";
import CompanyFormDialog from "../../../pages/companies/CompanyFormDialog";
import { actionTypes } from "../../../../types/state";
import { useTranslation } from "react-i18next";
import { SelectedSuperAdminPermissions } from "../../../../types/user";
import { Typography } from "@mui/material";
const MenuLink = styled(Link)(() => {
  return {
    width: "100%",
  };
});

/** Vertical padding for drawer menu rows; fewer visible items get more spacing. */
function getMenuItemButtonPadding(visibleItemCount: number): string {
  if (visibleItemCount <= 6) return "7px 16px";
  if (visibleItemCount <= 12) return "5px 16px";
  if (visibleItemCount <= 14) return "4px 16px";
  return "0px 16px";
}

const DesktopNavbarMenuButton = styled(ListItem)(({ theme }) => {
  const isDark = theme.palette.mode === "dark";
  const activeFill = isDark ? "#0b1220" : "white";
  const activeFillFade = isDark ? "#0b1220dd" : "#fffd";
  return {
    "& .MuiListItemSecondaryAction-root": {
      opacity: 0,
      marginRight: "6px",
      transition: "0.5s",
      right: "8px",
      display: "flex",
      alignItems: "center",
    },
    "&:hover .MuiListItemSecondaryAction-root": {
      opacity: 1,
      marginRight: "0px",
    },
    "&.active": {
      backgroundColor: theme.palette.primary.main,
    },
    "&.active .MuiListItemButton-root": {
      backgroundImage: `linear-gradient(to left, ${activeFillFade}, ${activeFill})`,
      borderRight: `solid 2px ${theme.palette.primary.main}`,
    },
  };
});

export default function SideDrawerMenu({
  width,
  backendVersions,
}: {
  width?: number;
  backendVersions: IVersionsObject;
}) {
  const { t } = useTranslation();
  const { state, dispatch } = useAppContext();
  const { loggedInUserInfo, myCompanyProfile } = state;
  const currentPath = useLocation();
  const navigate = useNavigate();
  const [openVersionDialog, setOpenVersionDialog] = React.useState(false);
  const [openCompanyFormDialog, setOpenCompanyFormDialog] =
    React.useState(false);
  const isMobile = useIsMobileViewport();
  const isCompanyAdmin = loggedInUserInfo?.role == UserRoles.companyAdmin;
  const isSuperAdmin = loggedInUserInfo?.role == UserRoles.superadmin;

  const closeDrawer = () => {
    dispatch?.({ type: actionTypes.setOpenSideDrawer, value: false });
  };

  const handleMenuNavigate = (
    event: React.MouseEvent,
    path: string
  ) => {
    event.preventDefault();
    closeDrawer();
    navigate(path);
  };

  const visibleNavLinks = React.useMemo(
    () =>
      NavLinks.filter((nav) => {
        if (isMobile && !nav.mobileEnabled) return false;
        const hasRequiredPermissions =
          (isSuperAdmin &&
            !nav.requiredPermissions?.find((p) =>
              SelectedSuperAdminPermissions.includes(p)
            )) ||
          !nav.requiredPermissions?.some(
            (p) =>
              !loggedInUserInfo?.permissions
                ?.map((myP) => myP.permissionName)
                ?.includes(p)
          );
        const passesRoleGate =
          isSuperAdmin ||
          (!nav.onlySuperAdmin &&
            !(nav.onlyCompanyAdmin && !isCompanyAdmin) &&
            !(nav.onlyVendors && myCompanyProfile?.vendorCompanyId));
        return hasRequiredPermissions && passesRoleGate;
      }),
    [
      isMobile,
      isSuperAdmin,
      isCompanyAdmin,
      loggedInUserInfo,
      myCompanyProfile,
    ]
  );

  const menuItemButtonPadding = getMenuItemButtonPadding(
    visibleNavLinks.length
  );

  return (
    <Box
      role="presentation"
      onClick={closeDrawer}
      sx={{
        width: width || 270,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#303c51" : "#f9f9f9",
          mb: 1,
          minHeight: "65px",
          position: "relative",
          maxHeight: "200px",
          overflow: "hidden",
        }}
      >
        {myCompanyProfile?.logoUrl ? (
          <img
            src={myCompanyProfile?.logoUrl}
            alt="Intake Rate Logo"
            style={{
              width: "100%",
              height: "auto",
              marginBottom: "-4px",
            }}
          />
        ) : null}
        {myCompanyProfile?.logoUrl ? (
          <img
            src={myCompanyProfile?.logoUrl}
            alt="Intake Rate Logo"
            style={{
              position: "absolute",
              width: "100%",
              height: "auto",
              top: "50%",
              transform: "translate(0%, -50%)",
              left: "0px",
            }}
          />
        ) : null}
        {myCompanyProfile ? (
          <Box
            className="controls"
            sx={{
              position: "absolute",
              bottom: "0px",
              width: "100%",
              background: "linear-gradient(#0000, #0009, #000a)",
              color: "white",
              fontSize: "17px",
              fontWeight: "500",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "30px 20px 5px 20px",
              }}
            >
              {myCompanyProfile.name}
              <Box sx={{ flex: 1 }} />
              {loggedInUserInfo?.role == UserRoles.companyAdmin ||
              loggedInUserInfo?.role == UserRoles.superadmin ? (
                <IconButton
                  sx={{
                    "&:hover": {
                      background: "#fff3",
                    },
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    setOpenCompanyFormDialog(true);
                  }}
                >
                  <EditIcon sx={{ color: "white", fontSize: "17px" }} />
                </IconButton>
              ) : null}
            </Box>
          </Box>
        ) : null}
      </Box>
      <List sx={{ pl: 1,
        "& .MuiListItemButton-root": {
          padding: menuItemButtonPadding
        },
        "& .MuiListItemText-root": {
          marginTop: "3px !important",
          marginBottom: "3px !important",
        }
       }}>
        {visibleNavLinks.map((navItem) => (
          <div>
            {navItem.firstSuperAdminItem ? (
              <Box
                sx={{
                  borderBottom: "solid 1px #ddd",
                  pl: "4px",
                  mt: 1,
                  mb: "4px",
                  color: "warning.main",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <LocalPoliceIcon sx={{ fontSize: "16px", mr: "6px" }} />
                <Typography variant="caption">Super Admin Only</Typography>
              </Box>
            ) : null}
            <DesktopNavbarMenuButton
              key={navItem.menu_title}
              disablePadding
              secondaryAction={<ArrowForwardIcon color="primary" />}
              selected={
                currentPath?.pathname &&
                currentPath?.pathname.includes(navItem.path)
                  ? true
                  : false
              }
              className={
                currentPath?.pathname &&
                currentPath?.pathname.includes(navItem.path)
                  ? "active"
                  : ""
              }
            >
              <MenuLink
                to={navItem.path}
                onClick={(event) => handleMenuNavigate(event, navItem.path)}
              >
                <ListItemButton>
                  <ListItemIcon sx={{ minWidth: "45px" }}>
                    {navItem.menu_icon({
                      color:
                        currentPath?.pathname &&
                        currentPath?.pathname.includes(navItem.path)
                          ? "primary"
                          : "inherit",
                    })}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          color:
                            currentPath?.pathname &&
                            currentPath?.pathname.includes(navItem.path)
                              ? "primary.main"
                              : "inherit",
                        }}
                      >
                        {t(
                          isSuperAdmin
                            ? navItem.superAdminTitle || t(navItem.menu_title)
                            : isCompanyAdmin
                            ? navItem.companyAdminTitle || t(navItem.menu_title)
                            : navItem.menu_title
                        )}
                      </Box>
                    }
                    color="primary"
                  />
                </ListItemButton>
              </MenuLink>
            </DesktopNavbarMenuButton>
          </div>
        ))}
      </List>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        }}
      >
        <Box
          px={2}
          py={1}
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#1e293b" : "#e7f0f7",
            fontSize: "13px",
          }}
        >
          Fermentrics © 2026 All Rights Reserved.
          <Box
            mt={1}
            sx={{
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                cursor: "pointer",
                color: "#888888",
                "&:hover": { color: "info.main", textDecoration: "underline" },
                mb: "4px",
              }}
            >
              <a href="/terms-and-conditions">{t("Terms and Conditions")}</a>
            </Box>
            <Box
              sx={{
                cursor: "pointer",
                color: "#888888",
                "&:hover": { color: "info.main", textDecoration: "underline" },
                mb: "4px",
              }}
            >
              <a href="/privacy-policy">{t("Privacy Policy")}</a>
            </Box>
            <Box
              sx={{
                cursor: "pointer",
                color: "#888888",
                "&:hover": { color: "info.main", textDecoration: "underline" },
                
              }}
            >
              <a href="/change-logs">{t("Change Logs")}</a>
            </Box>
            <div style={{ flex: 1 }} />
          </Box>
        </Box>
      </Box>
      <VersionsDialog
        open={openVersionDialog}
        onClose={() => setOpenVersionDialog(false)}
        frontendVersions={{
          PRODUCT: window.env.PRODUCT || "dev",
          PRODUCT_VERSION: window.env.PRODUCT_VERSION || "0.0.0",
          GRPC_VERSION: window.env.GRPC_VERSION || "local",
          BUILD: window.env.BUILD || "now",
          COMMIT_TAG: window.env.COMMIT_TAG || "local",
        }}
        backendVersions={backendVersions || {}}
      />
      {openCompanyFormDialog && (
        <CompanyFormDialog
          company={myCompanyProfile}
          open
          onClose={() => setOpenCompanyFormDialog(false)}
          onUpdated={(updatedCompany) => {
            setOpenCompanyFormDialog(false);
          }}
        />
      )}
    </Box>
  );
}
